import { useState, useEffect, useCallback } from 'react';
import { llmService, type AnalysisResponse, type ShotData } from '@/services/llm.service';
import { toast } from '@/hooks/use-toast';

interface LLMError {
  message: string;
  code?: string;
  details?: unknown;
}

interface LLMMetrics {
  requestsPerMinute: number;
  averageResponseTime: number;
  activeModels: string[];
}

interface UseLLMOptions {
  autoConnect?: boolean;
  onAnalysis?: (analysis: AnalysisResponse) => void;
  onIntervention?: (intervention: string) => void;
  onError?: (error: LLMError) => void;
}

export function useLLM(options: UseLLMOptions = {}) {
  const {
    autoConnect = true,
    onAnalysis,
    onIntervention,
    onError
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [latestAnalysis, setLatestAnalysis] = useState<AnalysisResponse | null>(null);
  const [latestIntervention, setLatestIntervention] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<LLMMetrics | null>(null);

  useEffect(() => {
    if (!autoConnect) return;

    // Set up event listeners
    const handleConnected = () => {
      setIsConnected(true);
      toast({
        title: 'AI Coaching Connected',
        description: 'Real-time analysis is now available',
      });
    };

    const handleDisconnected = () => {
      setIsConnected(false);
      toast({
        title: 'AI Coaching Disconnected',
        description: 'Attempting to reconnect...',
        variant: 'destructive',
      });
    };

    const handleAnalysis = (analysis: AnalysisResponse) => {
      setLatestAnalysis(analysis);
      setIsAnalyzing(false);
      if (onAnalysis) {
        onAnalysis(analysis);
      }
    };

    const handleIntervention = (intervention: string) => {
      setLatestIntervention(intervention);
      if (onIntervention) {
        onIntervention(intervention);
      }
    };

    const handleError = (error: LLMError) => {
      console.error('LLM service error:', error);
      setIsAnalyzing(false);
      if (onError) {
        onError(error);
      } else {
        toast({
          title: 'AI Analysis Error',
          description: error.message || 'Failed to process request',
          variant: 'destructive',
        });
      }
    };

    // Wrap handlers to match expected signature
    const wrappedHandleAnalysis = (...args: unknown[]) => handleAnalysis(args[0] as AnalysisResponse);
    const wrappedHandleIntervention = (...args: unknown[]) => handleIntervention(args[0] as string);
    const wrappedHandleError = (...args: unknown[]) => handleError(args[0] as LLMError);

    llmService.on('connected', handleConnected);
    llmService.on('disconnected', handleDisconnected);
    llmService.on('analysis', wrappedHandleAnalysis);
    llmService.on('intervention', wrappedHandleIntervention);
    llmService.on('error', wrappedHandleError);

    // Check initial connection status
    setIsConnected(llmService.isConnected());

    // Clean up
    return () => {
      llmService.off('connected', handleConnected);
      llmService.off('disconnected', handleDisconnected);
      llmService.off('analysis', wrappedHandleAnalysis);
      llmService.off('intervention', wrappedHandleIntervention);
      llmService.off('error', wrappedHandleError);
    };
  }, [autoConnect, onAnalysis, onIntervention, onError]);

  /**
   * Analyze a shot
   */
  const analyzeShot = useCallback(async (shotData: ShotData, context?: Record<string, unknown>) => {
    setIsAnalyzing(true);
    try {
      if (isConnected) {
        // Use WebSocket for real-time response
        llmService.analyzeShotRealtime(shotData, context);
      } else {
        // Fall back to HTTP
        const analysis = await llmService.analyzeShot(shotData, context);
        setLatestAnalysis(analysis);
        setIsAnalyzing(false);
        if (onAnalysis) {
          onAnalysis(analysis);
        }
        return analysis;
      }
    } catch (error) {
      setIsAnalyzing(false);
      console.error('Failed to analyze shot:', error);
      toast({
        title: 'Analysis Failed',
        description: 'Unable to analyze shot. Please try again.',
        variant: 'destructive',
      });
      throw error;
    }
  }, [isConnected, onAnalysis]);

  /**
   * Generate coaching intervention
   */
  const generateIntervention = useCallback(async (
    shotData: ShotData,
    previousShots: ShotData[] = [],
    urgency: 'low' | 'medium' | 'high' = 'medium'
  ) => {
    try {
      if (isConnected) {
        // Use WebSocket for real-time response
        llmService.generateInterventionRealtime(shotData, previousShots, urgency);
      } else {
        // Fall back to HTTP
        const intervention = await llmService.generateIntervention(shotData, previousShots, urgency);
        setLatestIntervention(intervention);
        if (onIntervention) {
          onIntervention(intervention);
        }
        return intervention;
      }
    } catch (error) {
      console.error('Failed to generate intervention:', error);
      toast({
        title: 'Intervention Failed',
        description: 'Unable to generate coaching advice.',
        variant: 'destructive',
      });
      throw error;
    }
  }, [isConnected, onIntervention]);

  /**
   * Analyze pattern across multiple shots
   */
  const analyzePattern = useCallback(async (shots: ShotData[]) => {
    try {
      return await llmService.analyzePattern(shots);
    } catch (error) {
      console.error('Failed to analyze pattern:', error);
      toast({
        title: 'Pattern Analysis Failed',
        description: 'Unable to identify patterns.',
        variant: 'destructive',
      });
      throw error;
    }
  }, []);

  /**
   * Batch analyze multiple shots
   */
  const analyzeBatch = useCallback(async (shots: ShotData[]) => {
    try {
      return await llmService.analyzeBatch(shots);
    } catch (error) {
      console.error('Failed to analyze batch:', error);
      toast({
        title: 'Batch Analysis Failed',
        description: 'Unable to analyze multiple shots.',
        variant: 'destructive',
      });
      throw error;
    }
  }, []);

  /**
   * Fetch service metrics
   */
  const fetchMetrics = useCallback(async () => {
    try {
      const data = await llmService.getMetrics();
      setMetrics(data);
      return data;
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
      return null;
    }
  }, []);

  /**
   * Check service health
   */
  const checkHealth = useCallback(async () => {
    try {
      return await llmService.checkHealth();
    } catch (error) {
      console.error('Service health check failed:', error);
      return { status: 'unhealthy', error: error };
    }
  }, []);

  return {
    // State
    isConnected,
    isAnalyzing,
    latestAnalysis,
    latestIntervention,
    metrics,
    
    // Methods
    analyzeShot,
    generateIntervention,
    analyzePattern,
    analyzeBatch,
    fetchMetrics,
    checkHealth,
    
    // Service instance (for advanced usage)
    service: llmService
  };
}